import React, { useState, useEffect, useRef } from 'react';
import { Row } from 'react-bootstrap';
import { useStoreState } from 'easy-peasy';
import Plot from 'react-plotly.js';
import axios from '../../services/axios';
import './VideoPlayGraph.scss';
import Loader from '../widgets/Loader';
import { useModal } from '../../hooks/useModal';
import HelperMetiers360 from '../../services/HelpersMetiers360';
import DynamicTable from '../widgets/dynamicTable/DynamicTable';
import ButtonLoaderComponent from '../widgets/ButtonLoaderComponent';
import CsvDownloadComponent from '../widgets/csvDownload/CsvDownloadComponent';


const VideoPlayGraph = (props) => {
    const { clientsUniqueId = [], setIsFetching, isFetching, oneMonth, datesFilter, fromAdminWelcomePage = false, fromStatsPage = false } = props;

	const {apiData: {ACTIONS}} = useStoreState(state => state.actionSlugs);

	const [lineDisplay, setLineDisplay] = useState(null);
	const [barDisplay, setBarDisplay] = useState(null);
    const [isGeneralRankingFetching, setIsGeneralRankingFetching] = useState(false);
    const [viewsCsvTable, setViewsCsvTable] = useState(null);
    const [downloadViewsCSV, setDownloadViewsCSV] = useState(false);
    const [top5CsvTable, setTop5CsvTable] = useState(null);
    const [downloadTop5CSV, setDownloadTop5CSV] = useState(false);

	const timeoutDelay = fromStatsPage ? 1000 : 0;

	const isFirstRender = useRef(true);
    const { modalComponent, setModalShow, modalData, setModalData } = useModal();

    const fetchAndProcessData = async () => {
		if ((clientsUniqueId?.length > 0 || fromAdminWelcomePage) && !isFetching) {

            setIsFetching?.(true);
            setLineDisplay(null);
            setBarDisplay(null);

            const monthPlayRequest = axios.post(ACTIONS?.clients?.getProps?.getPlay?.url,
                { uuid: clientsUniqueId, oneMonth, startDate: datesFilter?.[0], endDate: datesFilter?.[1] },
                { responseType: "json" }
            );
            const topVideosRequest = axios.post(ACTIONS?.clients?.getProps?.getTopPlayed?.url,
                { uuid: clientsUniqueId, oneMonth, startDate: datesFilter?.[0], endDate: datesFilter?.[1] },
                { responseType: "json" }
            );

            const insertLineBreak = (label) => {
                if (label.length <= 25) return label;

                const middle = Math.floor(label.length / 2);
                const spaceBefore = label.lastIndexOf(' ', middle);
                const spaceAfter = label.indexOf(' ', middle);

                let breakIndex;

                // Find the space closest to the centre of the string
                if (spaceBefore === -1 && spaceAfter === -1) {
                    breakIndex = middle;
                } else if (spaceBefore === -1) {
                    breakIndex = spaceAfter;
                } else if (spaceAfter === -1) {
                    breakIndex = spaceBefore;
                } else {
                    breakIndex = (middle - spaceBefore < spaceAfter - middle) ? spaceBefore : spaceAfter;
                }

                return label.slice(0, breakIndex) + "<br>" + label.slice(breakIndex + 1);
            }

			Promise.all([monthPlayRequest, topVideosRequest])
				.then(([monthPlayResponse, topVideosResponse]) => {
					if (monthPlayResponse && monthPlayResponse?.length > 0) {
                        monthPlayResponse
                            ?.sort((a, b) => Number(a.day) - (b.day))
                            ?.sort((a, b) => Number(a.month) - (b.month))
                            ?.sort((a, b) => Number(a.year) - (b.year));

                        // Set table for csv 
                        const monthPlayWithNumericDate = monthPlayResponse.map(res => {
                            return {
                                date: String(res.day).padStart(2, '0') + "/"
                                    + String(res.month).padStart(2, '0') + "/"
                                    + res.year,
                                headsetViews: res.headsetViews,
                                webapp2Views: res.webapp2Views,
                                totalViews: (res.headsetViews ?? 0) + (res.webapp2Views ?? 0)
                            }
                        });
                        setViewsCsvTable(monthPlayWithNumericDate);

                        // Set data for graph
                        const xPlays = monthPlayResponse.map(monthRes => {
                            const stringDate = String(monthRes.month).padStart(2, '0') + "/"
                                + String(monthRes.day).padStart(2, '0') + "/"
                                + monthRes.year;
                            const date = new Date(stringDate);
                            return date.toLocaleDateString("fr-FR", { day: 'numeric', month: 'long', year: 'numeric' });
                        });

                        const headsetViews = monthPlayResponse.map(m => m.headsetViews ?? 0);
                        const webappViews = monthPlayResponse.map(m => m.webapp2Views ?? 0);

                        const tooMuchValues = xPlays.length > 31;
                        const divider = tooMuchValues ? Math.floor(xPlays.length / 15) : 2;

                        const tickvals = xPlays;
                        const ticktext = xPlays?.map((date, index) => index % divider === 0 ? date : '');

                        setLineDisplay(
                            <Plot
                                data={[
                                    {
                                        x: xPlays,
                                        y: headsetViews,
                                        name: 'Casque',
                                        type: 'bar',
                                        marker: { color: 'rgb(99, 132, 255)' },
                                        hovertext: xPlays,
                                        hoverinfo: 'text+y+name',
                                    },
                                    {
                                        x: xPlays,
                                        y: webappViews,
                                        name: 'Webapp',
                                        type: 'bar',
                                        marker: { color: 'rgb(207, 119, 255)' },
                                        hovertext: xPlays,
                                        hoverinfo: 'text+y+name',
                                    }
                                ]}
                                layout={{
                                    barmode: 'stack',
                                    autosize: true,
                                    margin: { l: 50, r: 50, b: 120, t: 50, pad: 4 },
                                    xaxis: {
                                        tickvals: tickvals,
                                        ticktext: ticktext,
                                        tickangle: -45,
                                    },
                                    hoverlabel: { bgcolor: 'rgb(255, 255, 255)' },
                                    legend: { orientation: 'h', y: -0.3 },
                                    title: { text: 'Lectures XP par jour (Casque + Webapp)' },
                                }}
                                useResizeHandler={true}
                            />
                        );
					}

					if (topVideosResponse && topVideosResponse?.length > 0) {
                        topVideosResponse.sort((a, b) => b.views - a.views);
                        const xBar = topVideosResponse.map(topVideoRes => insertLineBreak(topVideoRes.videoName));
                        const yBar = topVideosResponse.map(topVideoRes => topVideoRes.views);

                        // Set table for csv
                        setTop5CsvTable(topVideosResponse);

                        setBarDisplay(
                            <Plot
                                data={[{
                                    x: xBar, y: yBar, type: 'bar',
                                    marker: { color: 'rgb(207, 119, 255)' }
                                }]}
                                layout={{
                                    autosize: true,
                                    margin: { l: 50, r: 50, b: 120, t: 50, pad: 4 },
                                    xaxis: { tickangle: -45 },
                                    hoverlabel: { bgcolor: 'rgb(255, 255, 255)' }
                                }}
                                useResizeHandler={true}
                            />
                        );
					}
				})
				.catch((error) => {
					console.error("Error fetching data:", error);
				})
				.finally(() => {
                    setIsFetching?.(false);
				});
		} else {
			setLineDisplay(null);
			setBarDisplay(null);
		}

	}

	useEffect(() => {
		let timeoutId;

		if ((clientsUniqueId?.length > 0 || fromAdminWelcomePage) && !isFetching) {
			timeoutId = setTimeout(() => {
				fetchAndProcessData();
			}, timeoutDelay);

			return () => clearTimeout(timeoutId);
		}
	}, [clientsUniqueId]);


    useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		if ((clientsUniqueId?.length > 0 || fromAdminWelcomePage) && !isFetching) {
			fetchAndProcessData();
		}
    }, [oneMonth, datesFilter]);


    const noData = (!clientsUniqueId || clientsUniqueId?.length == 0) && !fromAdminWelcomePage
        ? <p>Veuillez sélectionner au moins un établissement</p>
        : <p>Pas de données à afficher</p>;

    const fetchGeneralRanking = async () => {
        setIsGeneralRankingFetching(true);
        axios.post(ACTIONS?.clients?.getProps?.getTopPlayed?.url,
            { uuid: clientsUniqueId, oneMonth, startDate: datesFilter?.[0], endDate: datesFilter?.[1], all: true },
            { responseType: "json" }
        ).then(generalRanking => {
            const sortType = [
                {
                    value: 'videoName', label: 'Nom',
                    test: HelperMetiers360.isArrayContainsValue(generalRanking, "videoName"),
                    method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.videoName, b.videoName),
                    display: (video) => video.videoName
                },
                {
                    value: 'subHeading', label: 'Titre secondaire',
                    test: HelperMetiers360.isArrayContainsValue(generalRanking, "subHeading"),
                    method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.subHeading, b.subHeading),
                    display: (video) => video.subHeading
                },
                {
                    value: 'views', label: 'Nombre de vues',
                    test: HelperMetiers360.isArrayContainsValue(generalRanking, "views"),
                    method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.views, b.views),
                    display: (video) => video.views
                }
            ];

            setModalData({
                ...modalData,
                header: <>Classement général des expériences immersives vues</>,
                content: <DynamicTable
                    contentTable={generalRanking}
                    contentSort={sortType}
                    valueInitSort="views"
                    index='videoName'
                    filename="classement_general_experiences_immersives_vues"
                />,
                cancelButton: 'OK',
                size: 'lg'
            });
            setIsGeneralRankingFetching(false);
            setModalShow(true);
        });
    }

    const viewsNumberCsvSort = [
        {
            value: 'date',
            label: 'Date',
            test: HelperMetiers360.isArrayContainsValue(viewsCsvTable, "date"),
            display: (viewsNumberData) => viewsNumberData.date
        },
        {
            value: 'headsetViews',
            label: 'Nombre de vues casques',
            test: HelperMetiers360.isArrayContainsValue(viewsCsvTable, "headsetViews"),
            display: (viewsNumberData) => viewsNumberData.headsetViews
        },
        {
            value: 'webapp2Views',
            label: 'Nombre de vues webapp',
            test: HelperMetiers360.isArrayContainsValue(viewsCsvTable, "webapp2Views"),
            display: (viewsNumberData) => viewsNumberData.webapp2Views
        },
        {
            value: 'totalViews',
            label: 'Nombre de vues totales',
            test: HelperMetiers360.isArrayContainsValue(viewsCsvTable, "totalViews"),
            display: (viewsNumberData) => viewsNumberData.totalViews
        }
    ];

    const top5CsvSort = [
        {
            value: 'videoName',
            label: 'Expérience immersive',
            test: true,
            display: (top5Data) => top5Data.videoName
        },
        {
            value: 'subHeading',
            label: 'Titre secondaire',
            test: true,
            display: (top5Data) => top5Data.subHeading
        },
        {
            value: 'views',
            label: 'Nombre de vues',
            test: true,
            display: (top5Data) => top5Data.views
        }
    ];

    return (
		isFetching ?
			<Loader />
			: <>
                {modalComponent}
				<div className="stat-graph">
                    <h4>Nombre de lectures{!!oneMonth && " au cours des 30 derniers jours"}</h4>{!!oneMonth && <br />}
                    {lineDisplay && <div className='d-flex justify-content-start'>
                        {downloadViewsCSV
                            ? <CsvDownloadComponent
                                contentTable={viewsCsvTable}
                                contentSort={viewsNumberCsvSort}
                                setDownloadCSV={setDownloadViewsCSV}
                                filename={"graphe_nombre_lectures" + displayDateFilters(datesFilter?.[0], datesFilter?.[1], oneMonth)}
                            />
                            : <div onClick={() => setDownloadViewsCSV(true)} type="button" role="button">
                                <i className="fas fa-download mr-2" /><span className='text-decoration-underline'>Télécharger en csv</span>
                            </div>}
                    </div>}
                    {lineDisplay ?? <Row className='no-data'>{noData}</Row>}
				</div>
				<div className="stat-graph">
					<h4>Top 5 des expériences immersives vues (Casques + Webapp){!!oneMonth && " au cours des 30 derniers jours"}</h4>
                    {barDisplay && <div className='d-flex justify-content-start'>
                        {downloadTop5CSV
                            ? <CsvDownloadComponent
                                contentTable={top5CsvTable}
                                contentSort={top5CsvSort}
                                setDownloadCSV={setDownloadTop5CSV}
                                filename={"graphe_top_5_experiences_immersives_vues" + displayDateFilters(datesFilter?.[0], datesFilter?.[1], oneMonth)}
                            />
                            : <div onClick={() => setDownloadTop5CSV(true)} type="button" role="button">
                                <i className="fas fa-download mr-2" /><span className='text-decoration-underline'>Télécharger en csv</span>
                            </div>}
                    </div>}
                    {barDisplay}
                    {barDisplay
                        ? top5CsvTable.length === 5 && <ButtonLoaderComponent
                            variant='light' className='mt-2'
                            onClick={() => fetchGeneralRanking()}
                            isSending={isGeneralRankingFetching}
                            msg="Voir le classement général"
                        />
                        : <Row className='no-data'>{noData}</Row>
                    }
				</div>
			</>
	);

}

export const displayDateFilters = (minDate, maxDate, oneMonth = false) => {
    if (!maxDate) {
        maxDate = new Date();
    }
    if (oneMonth) {
        minDate = new Date();
        minDate.setDate(minDate.getDate() - 30);
    }
    if (minDate || maxDate) {
        const displayMinDate = minDate
            ? HelperMetiers360.getdisplayDateType(minDate)?.split(" ")[0]
            : "debut";
        const displayMaxDate = HelperMetiers360.getdisplayDateType(maxDate)?.split(" ")[0];
        return "_[" + displayMinDate + "-" + displayMaxDate + "]";
    }
    return "_[depuis_le_debut]";
}

export default VideoPlayGraph;